import { defineComponent, toRefs, PropType, computed, ref } from '@vue/composition-api';
import {
  UTM,
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  AcoFormData,
  FALL_BACK_TYPE_ENUM,
  DeepLinkWebFallBackTypes,
} from '@tt4b/ttam-enums';
import formItemRejectReason from '@/components/common/form-item-reject-reason/index.vue';
import {
  useCreativeFallBackValidate,
  useFallbackFormStatus,
  getOriginalQuery,
  param2Utm,
  generateCustomParams,
  useUtmUpdateHook,
} from '@tt4b/ttam-hooks';
import TtamBackgroupdContainer from '@/components/common/background-container/index.vue';

import CreativeDeepLink from '../index';
import CreativeWebsiteUrl from '../../creative-website-url/index';
import { parseUrl } from 'query-string';
const name = 'ttam-creative-productsales-fallback-compose';
export default defineComponent({
  name,
  components: {
    formItemRejectReason,
    TtamBackgroupdContainer,
    TtamCreativeDeepLinkFallbackInput: CreativeDeepLink.TtamCreativeDeepLinkFallbackInput,
    TtamCreativeDeepLinkFallbackTypeSelect: CreativeDeepLink.TtamCreativeDeepLinkFallbackTypeSelect,
    TtamCreativeUtmEntry: CreativeWebsiteUrl.TtamCreativeUtmEntry,
  },
  props: {
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    rejectReason: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { adgroupData, campaignData, creativeData } = toRefs(props);
    const commonProps = {
      adgroupData,
      campaignData,
      creativeData,
    };
    const shareUtmProps = {
      isOpenUrl: false,
      overrideUrl: false,
      openUrlProp: ref(''),
    };
    const { isShowFallbackType: fallbackVisible, isShowFallbackCustomUrl: fallbackUrlVisible } = useFallbackFormStatus({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
      creativeFormData: creativeData,
    });
    const originalQuery = getOriginalQuery({
      creativeData,
      ...shareUtmProps,
    });
    const { handleUtmUpdate, checkEmitUtm, hasAppliedUtm, handleAppliedUtm, handleBuilderUtm } = useUtmUpdateHook({
      formData: creativeData,
      emit,
      isVsa: true,
      originalQuery,
      setFormData: () => () => ({}),
      ...shareUtmProps,
    });

    const _fallbackType = computed({
      get: () => {
        if (fallbackUrlVisible.value) {
          // CustomLink
          return CreativeDeepLink.FALL_BACK_TYPE_CUSTOM_WEB;
        }
        return creativeData.value.fallbackType || '';
      },
      set: val => {
        let paramsType = DeepLinkWebFallBackTypes.DefaultLink;

        switch (val) {
          case FALL_BACK_TYPE_ENUM.fallbackWeb:
            paramsType = DeepLinkWebFallBackTypes.CatalogueLink;
            break;
          case CreativeDeepLink.FALL_BACK_TYPE_CUSTOM_WEB:
            paramsType = DeepLinkWebFallBackTypes.CustomeLink;
            break;
          default:
            paramsType = DeepLinkWebFallBackTypes.DefaultLink;
        }

        const fallbackType =
          val === FALL_BACK_TYPE_ENUM.fallbackApp ? FALL_BACK_TYPE_ENUM.fallbackApp : FALL_BACK_TYPE_ENUM.fallbackWeb;

        const changeData: Partial<{
          fallbackType: number;
          paramsType: number;
          externalUrl: string;
          utms: UTM[];
        }> = {
          fallbackType,
          paramsType,
        };
        if (val === FALL_BACK_TYPE_ENUM.fallbackApp || FALL_BACK_TYPE_ENUM.fallbackWeb) {
          changeData.externalUrl = '';
          changeData.utms = [];
        }

        emit('fallback-type-change', changeData);
      },
    });

    const _fallbackUrl = computed({
      get: () => creativeData.value.externalUrl,
      set: (newV: string) => {
        handleUtmUpdate(newV);
        checkEmitUtm(newV);
        emit('fallback-url-change', newV);
      },
    });

    const handleApplyUtm = (newParams: string): void => {
      const hasUrlScene = _fallbackType.value === CreativeDeepLink.FALL_BACK_TYPE_CUSTOM_WEB;
      if (!hasAppliedUtm.value) {
        handleAppliedUtm(true);
      }

      if (hasUrlScene) {
        const { url } = parseUrl(_fallbackUrl.value);
        handleBuilderUtm(true);
        const originCustomParams = generateCustomParams(originalQuery.value);
        _fallbackUrl.value = originCustomParams
          ? `${url}?${originCustomParams}&${newParams.slice(1)}`
          : `${url}${newParams}`;
      } else {
        const newUtms = param2Utm(newParams);
        emit('utms-change', newUtms);
      }
    };

    const { fallBackTypeValidator, fallBackUrlValidator } = useCreativeFallBackValidate({
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
    });

    const { options: fallbackTypeOptions } = CreativeDeepLink.useFallBackTypePropsOptions(commonProps);
    const { label: fallbackTypeLabel } = CreativeDeepLink.useFallBackTypePropsLabel(commonProps);

    return {
      name,
      _fallbackType,
      _fallbackUrl,

      fallBackTypeValidator,
      fallBackUrlValidator,

      fallbackVisible,
      fallbackUrlVisible,
      fallbackTypeOptions,
      fallbackTypeLabel,

      handleApplyUtm,
      originalQuery,
    };
  },
});
